import memoizeOne from 'memoize-one';
import { fg } from '@atlassian/jira-feature-gating';
import type { LayoutFields } from './types.tsx';

// Issue Field (custom field) types (field.schema.type)
export const ISSUE_LINK_SEARCH = 'issue-link-search';
export const ISSUE_FIELD_NUMBER_CF_TYPE = 'number';
export const ISSUE_FIELD_STRING_CF_TYPE = 'string';
export const ISSUE_FIELD_DATETIME_CF_TYPE = 'datetime';
export const ISSUE_FIELD_DATE_CF_TYPE = 'date';
export const ISSUE_FIELD_SINGLE_SELECT_CF_TYPE = 'option2';
export const ISSUE_FIELD_SINGLE_SELECT_CF_TYPE_NEW = 'option'; // graphql currently returns this type
export const ISSUE_FIELD_ANY_CF_TYPE = 'any';
export const ISSUE_FIELD_MULTI_SELECT_CF_TYPE = 'array';
export const ISSUE_FIELD_USER_CF_TYPE = 'user';
export const ISSUE_FIELD_SINGLE_GROUP_CF_TYPE = 'group';
export const ISSUE_FIELD_OBJECT_CF_TYPE = 'object';
export const ISSUE_FIELD_GROUPS_CF_TYPE = 'group-list';
export const ISSUE_FIELD_STRINGS_CF_TYPE = 'array-string';
export const ISSUE_FIELD_VERSION_CF_TYPE = 'version';
export const ISSUE_FIELD_PARENT_ISSUES_TYPE = 'parent-issues'; // Remove on FD-30157 clean-up
export const APPROVALS_PANEL_TYPE = 'approval-panel';
export const ISSUE_FIELD_PROJECT_CF_TYPE = 'project';
export const ISSUE_FIELD_GOALS_CF_TYPE = 'array';

// GraphQL Field keys
export const AFFECTED_SERVICES_GQL_FIELD = 'JiraAffectedServicesField';
export const ASSIGNEE_GQL_FIELD = 'JiraAssigneeField';
export const ATTACHMENTS_GQL_FIELD = 'JiraAttachmentsField';
export const ATLASSIAN_TEAM_GQL_FIELD = 'JiraAtlassianTeamField';
export const BOOLEAN_GQL_FIELD = 'JiraBooleanField';
export const CASCADING_SELECT_GQL_FIELD = 'JiraCascadingSelectField';
export const CHECKBOX_GQL_FIELD = 'JiraCheckboxesField';
export const CHILDREN_EXCEEDING_LIMIT_GQL_FIELD = 'JiraChildIssuesExceedingLimit';
export const CHILDREN_WITHIN_LIMIT_GQL_FIELD = 'JiraChildIssuesWithinLimit';
export const CMDB_GQL_FIELD = 'JiraCMDBField';
export const COLOR_GQL_FIELD = 'JiraColorField';
export const COMPONENTS_GQL_FIELD = 'JiraComponentsField';
export const CONNECT_DATE_TIME_GQL_FIELD = 'JiraConnectDateTimeField';
export const CONNECT_NUMBER_GQL_FIELD = 'JiraConnectNumberField';
export const CONNECT_MULTI_SELECT_GQL_FIELD = 'JiraConnectMultipleSelectField';
export const CONNECT_RICH_TEXT_GQL_FIELD = 'JiraConnectRichTextField';
export const CONNECT_SINGLE_SELECT_GQL_FIELD = 'JiraConnectSingleSelectField';
export const CONNECT_TEXT_GQL_FIELD = 'JiraConnectTextField';
export const DATE_PICKER_GQL_FIELD = 'JiraDatePickerField';
export const DATE_TIME_GQL_FIELD = 'JiraDateTimePickerField';
export const EPIC_LINK_GQL_FIELD = 'JiraEpicLinkField';
export const FLAG_GQL_FIELD = 'JiraFlagField';
export const ISSUE_LINK_GQL_FIELD = 'JiraIssueLinkField';
export const ISSUE_RESTRICTION_GQL_FIELD = 'JiraIssueRestrictionField';
export const ISSUE_TYPE_GQL_FIELD = 'JiraIssueTypeField';
export const LABEL_GQL_FIELD = 'JiraLabelsField';
export const MULTI_SELECT_GQL_FIELD = 'JiraMultipleSelectField';
export const MULTI_USER_PICKER_GQL_FIELD = 'JiraMultipleSelectUserPickerField';
export const MULTI_VERSION_GQL_FIELD = 'JiraMultipleVersionPickerField';
export const MULTI_GROUP_PICKER_GQL_FIELD = 'JiraMultipleGroupPickerField';
export const NUMBER_GQL_FIELD = 'JiraNumberField';
export const ORIGINAL_ESTIMATE_GQL_FIELD = 'JiraOriginalTimeEstimateField';
export const PARENT_GQL_FIELD = 'JiraParentIssueField';
export const PEOPLE_GQL_FIELD = 'JiraPeopleField';
export const PRIORITY_GQL_FIELD = 'JiraPriorityField';
export const PROFORMA_FORMS_GQL_FIELD = 'JiraProformaFormsField';
export const PROJECT_GQL_FIELD = 'JiraProjectField';
export const RADIO_GQL_FIELD = 'JiraRadioSelectField';
export const REPORTER_GQL_FIELD = 'JiraReporterField';
export const REQUEST_TYPE_GQL_FIELD = 'JiraRequestTypeField';
export const RESOLUTION_GQL_FIELD = 'JiraResolutionField';
export const RICH_TEXT_GQL_FIELD = 'JiraRichTextField';
export const SECURITY_GQL_FIELD = 'JiraSecurityLevelField';
export const SINGLE_GROUP_PICKER_GQL_FIELD = 'JiraSingleGroupPickerField';
export const SINGLE_SELECT_GQL_FIELD = 'JiraSingleSelectField';
export const SPRINT_GQL_FIELD = 'JiraSprintField';
export const STATUS_GQL_FIELD = 'JiraStatusField';
export const TEAM_GQL_FIELD = 'JiraTeamField';
export const TEAM_VIEW_GQL_FIELD = 'JiraTeamViewField';
export const TEXT_GQL_FIELD = 'JiraSingleLineTextField';
export const TIME_TRACKING_GQL_FIELD = 'JiraTimeTrackingField';
export const URL_GQL_FIELD = 'JiraUrlField';
export const USER_PICKER_GQL_FIELD = 'JiraSingleSelectUserPickerField';
export const VERSION_GQL_FIELD = 'JiraSingleVersionPickerField';
export const VOTES_GQL_FIELD = 'JiraVotesField';
export const WATCHES_GQL_FIELD = 'JiraWatchesField' as const;
export const JSM_ASSETS_GQL_FIELD = 'JiraAssetField';
export const JSM_PEOPLE_GQL_FIELD = 'JiraServiceManagementPeopleField';
export const JSM_ORGANIZATION_GQL_FIELD = 'JiraServiceManagementOrganizationField';
export const FORGE_STRING_GQL_FIELD = 'JiraForgeStringField';
export const FORGE_STRINGS_GQL_FIELD = 'JiraForgeStringsField';
export const FORGE_NUMBER_GQL_FIELD = 'JiraForgeNumberField';
export const FORGE_DATETIME_GQL_FIELD = 'JiraForgeDatetimeField';
export const FORGE_DATE_GQL_FIELD = 'JiraForgeDateField';
export const FORGE_OBJECT_GQL_FIELD = 'JiraForgeObjectField';
export const FORGE_GROUP_GQL_FIELD = 'JiraForgeGroupField';
export const FORGE_USER_GQL_FIELD = 'JiraForgeUserField';
export const FORGE_GROUPS_GQL_FIELD = 'JiraForgeGroupsField';
export const FORGE_USERS_GQL_FIELD = 'JiraForgeUsersField';
export const JSM_APPROVAL_GQL_FIELD = 'JiraServiceManagementApprovalField';
export const JSM_USER_APPROVER_PRINCIPAL_FIELD = 'JiraServiceManagementUserApproverPrincipal';
export const JSM_GROUP_APPROVER_PRINCIPAL_FIELD = 'JiraServiceManagementGroupApproverPrincipal';
export const JSM_REQUEST_FEEDBACK_GQL_FIELD = 'JiraServiceManagementRequestFeedbackField';
export const JSM_REQUEST_LANGUAGE_GQL_FIELD = 'JiraServiceManagementRequestLanguageField';
export const JSM_REQUEST_TYPE_GQL_FIELD = 'JiraServiceManagementRequestTypeField';
export const JSM_RESPONDERS_GQL_FIELD = 'JiraServiceManagementRespondersField';
export const JSM_USER_RESPONDER_GQL_FIELD = 'JiraServiceManagementUserResponder';
export const JSM_ENTITLEMENTS_GQL_FIELD = 'JiraServiceManagementEntitlementField';
export const JSM_SENTIMENT_GQL_FIELD = 'JiraServiceManagementSentimentField';
export const JSM_TEAM_RESPONDER_FIELD = 'JiraServiceManagementTeamResponder';
export const JSM_MAJOR_INCIDENT_FIELD = 'JiraServiceManagementMajorIncidentField';
export const DATA_CLASSIFICATION_FIELD = 'JiraDataClassificationField';
export const GOALS_GQL_FIELD = 'JiraGoalsField';
export const DURATION_GQL_FIELD = 'JiraDurationField';
export const JCS_ORGANIZATION_GQL_FIELD = 'JiraCustomerServiceOrganizationField';

// Forge Custom Field
export const FORGE_CUSTOM_FIELD_TYPE = 'forgeCustomField';

// Custom simplified forge field types used to determine the field type (JSM experiences)
export const FORGE_STRING_TYPE = 'forge/string' as const;
export const FORGE_STRING_LIST_TYPE = 'forge/string-list' as const;
export const FORGE_NUMBER_TYPE = 'forge/number' as const;
export const FORGE_DATE_TYPE = 'forge/date' as const;
export const FORGE_DATETIME_TYPE = 'forge/datetime' as const;
export const FORGE_USER_TYPE = 'forge/user' as const;
export const FORGE_USER_LIST_TYPE = 'forge/user-list' as const;
export const FORGE_GROUP_TYPE = 'forge/group' as const;
export const FORGE_GROUP_LIST_TYPE = 'forge/group-list' as const;
// Teams in Jira
export const TEAMS_PLATFORM_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:atlassian-team' as const;

// Custom field types (field.schema.custom)
export const APPROVALS_CF_TYPE = 'com.atlassian.servicedesk.approvals-plugin:sd-approvals' as const;
export const CATEGORY_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:jwm-category' as const;
export const PEOPLE_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:people' as const;
export const NUMBER_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:float' as const;
export const STORY_POINT_ESTIMATE_CF_TYPE = 'com.pyxis.greenhopper.jira:jsw-story-points' as const;
export const URL_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:url' as const;
export const TEXT_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:textfield' as const;
export const TEXT_AREA_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:textarea' as const;
export const TEXT_READONLY_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:readonlyfield' as const;
export const LABELS_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:labels' as const;
export const DATE_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:datepicker' as const;
export const DATETIME_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:datetime' as const;
export const RADIO_BUTTONS_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:radiobuttons' as const;
export const MULTI_CHECKBOXES_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:multicheckboxes' as const;
export const MULTI_SELECT_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:multiselect' as const;
export const SELECT_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:select' as const;
export const EPIC_STATUS_TYPE = 'com.pyxis.greenhopper.jira:gh-epic-status' as const;
export const MULTI_VERSION_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:multiversion' as const;
export const VERSION_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:version' as const;
export const GROUP_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:grouppicker' as const;
export const MULTI_GROUP_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:multigrouppicker' as const;
export const USER_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:userpicker' as const;
export const MULTI_USER_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:multiuserpicker' as const;
export const PARTICIPANTS_CF_TYPE = 'com.atlassian.jira.toolkit:participants' as const;
export const FLAGGED_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:flagged' as const;
export const TEAM_CF_TYPE = 'com.atlassian.teams:rm-teams-custom-field-team' as const;
export const PARENT_CF_TYPE = 'com.atlassian.jpo:jpo-custom-field-parent' as const;
export const PARENT_ISSUES_CF_TYPE = 'com.atlassian.graphql:parent-issues' as const;
export const BASELINE_START_CF_TYPE = 'com.atlassian.jpo:jpo-custom-field-baseline-start' as const;
export const BASELINE_END_CF_TYPE = 'com.atlassian.jpo:jpo-custom-field-baseline-end' as const;
export const COLOR_CF_TYPE = 'com.pyxis.greenhopper.jira:jsw-issue-color' as const;
export const EPIC_COLOR_TYPE = 'com.pyxis.greenhopper.jira:gh-epic-color' as const;
export const ASSETS_CF_TYPE = 'com.atlassian.servicedesk.assets-plugin:assetfield' as const;
export const SERVICE_ENTITY_CF_TYPE =
	'com.atlassian.jira.plugins.service-entity:service-entity-field-cftype' as const;
export const REQUEST_PARTICIPANTS_CF_TYPE =
	'com.atlassian.servicedesk:sd-request-participants' as const;
// Long-standing field used in Jira Service Management projects
// Stores multiple customer organizations associated with an issue
export const ORGANIZATIONS_CF_TYPE = 'com.atlassian.servicedesk:sd-customer-organizations' as const;
// Used in Jira Customer Service projects as of late 2024/early 2025
// Stores a single customer organization. This is then used to display the organizations' details on the issue view.
export const ORGANIZATION_CF_TYPE =
	'com.atlassian.servicedesk.customer-context:sd-csm-organization' as const;
export const REQUEST_LANGUAGE_CF_TYPE =
	'com.atlassian.servicedesk.servicedesk-lingo-integration-plugin:sd-request-language' as const;
export const REQUEST_FEEDBACK_CF_TYPE = 'com.atlassian.servicedesk:sd-request-feedback' as const;
export const REQUEST_FEEDBACK_DATE_CF_TYPE =
	'com.atlassian.servicedesk:sd-request-feedback-date' as const;
export const REQUEST_TYPE_CF_TYPE = 'com.atlassian.servicedesk:vp-origin' as const;
export const APPROVERS_LIST_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:servicedesk-approvers-list' as const;
export const CASCADING_SELECT_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:cascadingselect' as const;
export const RANK_CF_TYPE = 'com.pyxis.greenhopper.jira:gh-lexo-rank' as const;
export const CMDB_OBJECT_CF_TYPE = 'com.atlassian.jira.plugins.cmdb:cmdb-object-cftype' as const;
export const READ_ONLY_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:readonlyfield' as const;
export const PROJECT_PICKER_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:project' as const;
export const RESPONDERS_CF_TYPE =
	'com.atlassian.jira.modules.servicemanagement.responders-entity:responders-entity-field-cftype' as const;
export const MAJOR_INCIDENT_CF_TYPE =
	'com.atlassian.jira.modules.servicemanagement.major-incident-entity:major-incident-entity-field-cftype' as const;
export const JIM_PLUGIN_IMPORT_ID_LINK_CF_TYPE =
	'com.atlassian.jira.plugins.jim-plugin:bug-importid' as const;
export const SYSTEM_IMPORT_ID_LINK_CF_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:importid' as const;
export const UUID_CF_TYPE = 'com.atlassian.jconnect.jconnect-plugin:uuid' as const;
export const ENTITLEMENT_CF_TYPE =
	'com.atlassian.servicedesk.customer-context:sd-csm-entitlement' as const;
export const SENTIMENT_CF_TYPE = 'com.atlassian.servicedesk.sentiment:sd-sentiment' as const;

export const MESSAGE_VIEW_CF_TYPE = 'com.atlassian.jira.toolkit:viewmessage' as const;
export const MESSAGE_EDIT_CF_TYPE = 'com.atlassian.jira.toolkit:message' as const;
export const LAST_UPDATER_OR_COMMENTER_CF_TYPE =
	'com.atlassian.jira.toolkit:lastupdaterorcommenter' as const;
export const ATTACHMENTS_NUMBER_CF_TYPE = 'com.atlassian.jira.toolkit:attachments' as const;
export const LAST_COMMENT_DATE_CF_TYPE = 'com.atlassian.jira.toolkit:LastCommentDate' as const;
export const USER_PROPERTY_CF_TYPE = 'com.atlassian.jira.toolkit:userproperty' as const;
export const ASSIGNEE_DOMAIN_CF_TYPE = 'com.atlassian.jira.toolkit:assigneedomain' as const;
export const REPORTER_DOMAIN_CF_TYPE = 'com.atlassian.jira.toolkit:reporterdomain' as const;
export const COMMENTS_NUMBER_CF_TYPE = 'com.atlassian.jira.toolkit:comments' as const;
export const DAYS_LAST_COMMENTED_CF_TYPE = 'com.atlassian.jira.toolkit:dayslastcommented' as const;
export const LAST_USER_COMMENTED_CF_TYPE = 'com.atlassian.jira.toolkit:lastusercommented' as const;

export const SLA_CF_TYPE = 'com.atlassian.servicedesk:sd-sla-field' as const;
export const SD_SERVICELEVELAGREEMENT = 'sd-servicelevelagreement' as const;
export const TIME_IN_STATUS_CF_TYPE = 'com.atlassian.jira.ext.charting:timeinstatus' as const;
export const FIRST_RESPONSE_DATE_CF_TYPE =
	'com.atlassian.jira.ext.charting:firstresponsedate' as const;
export const DEV_SUMMARY_CF_TYPE =
	'com.atlassian.jira.plugins.jira-development-integration-plugin:devsummarycf' as const;
export const CHILDREN_ISSUES_CF_TYPE = 'com.atlassian.graphql:children-issues' as const;
export const EPIC_ISSUES_CF_TYPE = 'com.atlassian.graphql:epic-issues' as const;
export const PORTFOLIO_ISSUES_CF_TYPE = 'com.atlassian.graphql:portfolio-child-issues' as const;
export const GOALS_CF_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:goals' as const;

// System field types (field.schema.system)
export const ASSIGNEE_TYPE = 'assignee' as const;
export const REPORTER_TYPE = 'reporter' as const;
export const DUE_DATE_TYPE = 'duedate' as const;
export const PRIORITY_TYPE = 'priority' as const;
export const LABELS_TYPE = 'labels' as const;
export const CREATED = 'created' as const;
export const UPDATED = 'updated' as const;
export const RESOLUTION_DATE = 'resolutiondate' as const;
export const COMPONENTS_TYPE = 'components' as const;
export const SERVICEDESK_APPROVAL_TYPE = 'servicedesk-approvals' as const;
export const SERVICEDESK_PRACTICES_TYPE = 'servicedesk-practices' as const;
export const SERVICEDESK_INCIDENT_LINKING_TYPE = 'servicedesk-incident-linking' as const;
export const SERVICEDESK_INCIDENT_ESCALATE_TYPE = 'servicedesk-incident-escalate' as const;
export const SERVICEDESK_INCIDENT_MANAGEMENT_TYPE = 'servicedesk-incident-management' as const;
export const DEV_SUMMARY_TYPE = 'devSummary' as const;
export const RELEASES_TYPE = 'releases' as const;
export const SPRINT_TYPE = 'com.pyxis.greenhopper.jira:gh-sprint' as const;
export const STORY_POINTS_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:storypoint' as const;
export const EPIC_TYPE = 'com.pyxis.greenhopper.jira:gh-epic-link' as const;
export const EPIC_NAME_TYPE = 'com.pyxis.greenhopper.jira:gh-epic-label' as const;
export const FIX_VERSIONS_TYPE = 'fixVersions' as const;
export const AFFECTS_VERSIONS_TYPE = 'versions' as const;
export const DESCRIPTION_TYPE = 'description' as const;
export const ENVIRONMENT_TYPE = 'environment' as const;
export const WATCHES_TYPE = 'watches' as const;
export const SUMMARY_TYPE = 'summary' as const;
export const PROFORMA_FORMS = 'proforma-forms' as const;
export const RESOLUTION_TYPE = 'resolution' as const;
export const TIME_TRACKING_TYPE = 'timetracking' as const;
export const TIME_ESTIMATE_TYPE = 'timeoriginalestimate' as const;
export const STATUS_TYPE = 'status' as const;
export const STATUS_CATEGORY_TYPE = 'statusCategory' as const;
export const ISSUE_TYPE = 'issuetype' as const;
export const ISSUE_KEY_TYPE = 'issuekey' as const;
export const CHILDREN_ISSUES = 'children-issues' as const;
export const PORTFOLIO_CHILD_ISSUES = 'portfolio-child-issues' as const;
export const EPIC_ISSUES = 'epic-issues' as const;
export const HIERARCHY_LEVEL_BELOW = 'hierarchy-level-below' as const;
export const GRAPHQL_HIERARCHY_LEVEL_BELOW = 'com.atlassian.graphql:hierarchy-level-below' as const;
export const HIERARCHY_LEVEL_ABOVE = 'hierarchy-level-above' as const;
export const GRAPHQL_HIERARCHY_LEVEL_ABOVE = 'com.atlassian.graphql:hierarchy-level-above' as const;
export const HIERARCHY_LEVEL_SAME_LEVEL = 'hierarchy-level-same-level' as const;
export const GRAPHQL_HIERARCHY_LEVEL_SAME_LEVEL =
	'com.atlassian.graphql:hierarchy-level-same-level' as const;
export const CUSTOMER_CONTEXT_TYPE = 'CUSTOMER_CONTEXT' as const;
export const SLA_PANEL_TYPE = 'SLA_PANEL' as const;
export const LINKED_ALERTS_TYPE = 'LINKED_ALERTS' as const;
export const CHAT_CHANNEL_TYPE = 'CHAT_CHANNEL' as const;
export const MSTEAMS_CHANNEL_TYPE = 'MSTEAMS_CHANNEL' as const;
export const ZOOM_MEETING_TYPE = 'ZOOM_MEETING' as const;
export const MANAGE_STAKEHOLDERS_TYPE = 'MANAGE_STAKEHOLDERS' as const;
export const KNOWLEDGE_BASE_TYPE = 'KNOWLEDGE_BASE' as const;
export const JOURNEY_PANEL_TYPE = 'JOURNEY_PANEL' as const;
export const ASSET_ONBOARDING_TYPE = 'ASSET_ONBOARDING' as const;
export const SIMILAR_ISSUES_TYPE = 'similar-issues' as const;
export const SUBTASKS_TYPE = 'subtasks' as const;
export const VOTES_TYPE = 'votes' as const;
export const PARENT_TYPE = 'parent' as const;
export const PARENT_ISSUE_TYPE = 'parent-issue' as const; // Remove on FD-30157 clean-up
export const PROJECT_TYPE = 'project' as const;
export const WORKLOG = 'worklog' as const;
export const TIME_SPENT_TYPE = 'timespent' as const;
export const DATA_CLASSIFICATION_TYPE = 'dataclassification' as const;
export const SENTIMENT_TYPE = 'SENTIMENT' as const;
export const DETAILS_PANEL_IN_EDIT_MODE = 'DETAILS_PANEL_IN_EDIT_MODE' as const;
export const ARE_FIELDS_REORDERED = 'ARE_FIELDS_REORDERED' as const;
export const HIDE_EMPTY_FIELDS = 'HIDE_EMPTY_FIELDS' as const;

// Ecosystem
export const ECOSYSTEM_GLANCE_TYPE = 'ecosystemGlance' as const;
export const FORGE_GLANCE_TYPE = 'forgeGlance' as const;
export const ECOSYSTEM_CONTEXT_TYPE = 'ecosystemContext' as const;
export const FORGE_CONTEXT_TYPE = 'forgeContext' as const;

// Issue restriction next gen
export const ISSUE_RESTRICTION_TYPE = 'issuerestriction' as const;

// Security level classic
export const SECURITY_LEVEL_TYPE = 'security' as const;
// Legacy GraphQl overrides the type of the security level field
export const SECURITY_LEVEL_TYPE_OVERRIDE = 'securitylevel' as const;

// Issue Table
// src/packages/issue-table/src/model/fields/types.js
export const CREATOR_TYPE = 'creator' as const;

// Portfolio 3
// src/packages/portfolio-3/portfolio/src/common/api/types.js
export const TEAM_TYPE = 'team' as const;
export const ESTIMATE_TYPE = 'estimate' as const;
export const DUE_DATE_PORTFOLIO_TYPE = 'dueDate' as const;
export const TARGET_START_TYPE = 'targetStart' as const;
export const TARGET_END_TYPE = 'targetEnd' as const;

// Roadmap
export const START_DATE_TYPE = 'startDate' as const;
export const COLOR_TYPE = 'color' as const;

// Project Config
// src/packages/project-settings/common/src/consts.js
export const ATTACHMENT_TYPE = 'attachment' as const;
export const ISSUE_LINKS_TYPE = 'issuelinks' as const;
export const TIME_ESTIMATE_CONFIG_TYPE = 'timeestimate' as const;
export const COMMENT_TYPE = 'comment' as const;
export const WORK_LOG_TYPE = 'worklog' as const;

// Hierarchy unification parent field type
export const PARENT_FIELD_TYPE = 'parent' as const;

// Other
export const AGGREGATE_TIME_ORIGINAL_ESTIMATE = 'aggregatetimeoriginalestimate' as const;
export const AGGREGATE_TIME_SPENT = 'aggregatetimespent' as const;
export const AGGREGATE_TIME_ESTIMATE = 'aggregatetimeestimate' as const;
export const PROGRESS = 'progress' as const;
export const AGGREGATE_PROGRESS = 'aggregateprogress' as const;
export const WORKRATIO = 'workratio' as const;

/**
 * Fields shown in the issue layout
 *
 * Cannot declare as simple object - in case there is a FF in getLayoutItemMap() (which happens when we add a new field), because we cannot get FF value in SSR.
 * Memoization is needed not to rerender too many times.
 */
const getLayoutFieldTypesNonMemoized = (): LayoutFields => {
	const types: LayoutFields = {
		ASSIGNEE_TYPE,
		DUE_DATE_TYPE,
		PRIORITY_TYPE,
		REPORTER_TYPE,
		DESCRIPTION_TYPE,
		LABELS_TYPE,
		COMPONENTS_TYPE,
		SPRINT_TYPE,
		STORY_POINTS_TYPE,
		TEAMS_PLATFORM_CF_TYPE,
		FIX_VERSIONS_TYPE,
		AFFECTS_VERSIONS_TYPE,
		TIME_ESTIMATE_TYPE,
		TIME_TRACKING_TYPE,
		ENVIRONMENT_TYPE,
		DEV_SUMMARY_TYPE,
		APPROVALS_PANEL_TYPE,
		KNOWLEDGE_BASE_TYPE,
		SLA_PANEL_TYPE,
		LINKED_ALERTS_TYPE,
		CHAT_CHANNEL_TYPE,
		MSTEAMS_CHANNEL_TYPE,
		ZOOM_MEETING_TYPE,
		MANAGE_STAKEHOLDERS_TYPE,
		ASSETS_CF_TYPE,
		RESPONDERS_CF_TYPE,
		MAJOR_INCIDENT_CF_TYPE,
		COLOR_CF_TYPE,
		EPIC_COLOR_TYPE,
		RELEASES_TYPE,
		BASELINE_START_CF_TYPE,
		BASELINE_END_CF_TYPE,
		LABELS_CF_TYPE,
		TEXT_CF_TYPE,
		MULTI_USER_CF_TYPE,
		PARTICIPANTS_CF_TYPE,
		URL_CF_TYPE,
		NUMBER_CF_TYPE,
		STORY_POINT_ESTIMATE_CF_TYPE,
		SELECT_CF_TYPE,
		EPIC_STATUS_TYPE,
		MULTI_SELECT_CF_TYPE,
		ECOSYSTEM_GLANCE_TYPE,
		FORGE_CUSTOM_FIELD_TYPE,
		ISSUE_FIELD_USER_CF_TYPE,
		ISSUE_FIELD_GROUPS_CF_TYPE,
		ISSUE_FIELD_STRINGS_CF_TYPE,
		FORGE_GLANCE_TYPE,
		TEXT_AREA_CF_TYPE,
		MULTI_VERSION_CF_TYPE,
		VERSION_CF_TYPE,
		DATE_CF_TYPE,
		GROUP_CF_TYPE,
		MULTI_GROUP_CF_TYPE,
		USER_CF_TYPE,
		DATETIME_CF_TYPE,
		MULTI_CHECKBOXES_CF_TYPE,
		RADIO_BUTTONS_CF_TYPE,
		TEAM_CF_TYPE,
		ISSUE_FIELD_NUMBER_CF_TYPE,
		ISSUE_FIELD_DATETIME_CF_TYPE,
		ISSUE_FIELD_STRING_CF_TYPE,
		ISSUE_FIELD_SINGLE_SELECT_CF_TYPE,
		ISSUE_FIELD_MULTI_SELECT_CF_TYPE,
		REQUEST_TYPE_CF_TYPE,
		SERVICE_ENTITY_CF_TYPE,
		REQUEST_PARTICIPANTS_CF_TYPE,
		ORGANIZATIONS_CF_TYPE,
		REQUEST_FEEDBACK_CF_TYPE,
		PEOPLE_CF_TYPE,
		REQUEST_LANGUAGE_CF_TYPE,
		ASSET_ONBOARDING_TYPE,
		APPROVERS_LIST_CF_TYPE,
		CASCADING_SELECT_CF_TYPE,
		PROJECT_PICKER_CF_TYPE,
		CMDB_OBJECT_CF_TYPE,
		READ_ONLY_CF_TYPE,
		MESSAGE_VIEW_CF_TYPE,
		MESSAGE_EDIT_CF_TYPE,
		EPIC_NAME_TYPE,
		ECOSYSTEM_CONTEXT_TYPE,
		CATEGORY_TYPE,
		ENTITLEMENT_CF_TYPE,
		GOALS_CF_TYPE,
		PARENT_TYPE,
		FORGE_CONTEXT_TYPE,
		SENTIMENT_CF_TYPE,

		/**
		 * WARNING:
		 *   Do not add feature flagged conditional spreads! This causes flow to implicitly create
		 *   union types that exponentially grow per flag.
		 *
		 *   To feature flag a new field type, add a conditional key to `LayoutFields` type.
		 *   Then use `Object.assign` to conditionally add the field.
		 *
		 *   @example
		 *      if (featureFlagEnabled()) {
		 *          Object.assign(types, { MY_NEW_FIELD: 'new-field' });
		 *      }
		 */
	};

	Object.assign(types, { SENTIMENT_TYPE });

	if (fg('jsm-journey-builder-in-issue-view')) {
		Object.assign(types, { JOURNEY_PANEL_TYPE });
	}

	return types;
};

export const getLayoutFieldTypes = memoizeOne(getLayoutFieldTypesNonMemoized);

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type {
	CheckFieldTypeDefinitions,
	CheckedCustomFieldTypeDefinitions,
	CheckedFieldDefinition,
	CheckedGraphQLFieldTypeDefinitions,
	CheckedLegacyFieldTypeDefinitions,
	CheckedSystemFieldTypeDefinitions,
	IgnoredFieldCheck,
} from './common/types/checked-fields';
